import { logger } from './logger';
import type { AnalysisEngine, PupilMode } from '@/types/optical';
import type { Locale } from '@/i18n/config';
import type { StartJobResponse, SSEEvent, ChatMessage, FileAttachment, ToolCallRecord, Verbosity } from '@/types/agent';
import { getSelectedService } from '@/hooks/useAnalysisService';
import { getDisabledCapabilities, getDisabledCount } from '@/hooks/useDisabledCapabilities';
import { getPromptVersion } from '@/hooks/usePromptVersion';
import { getMeritSeparate, meritSeparateBlockedReason } from '@/hooks/useMeritSeparate';
import { getAgentModel } from '@/hooks/useAgentModel';
import { BACKEND_URL } from '@/lib/config';
import type { ToleranceComputeRunParams, ToleranceApiRow, ToleranceApiWarning, ComputeToleranceAnalysisPayload, QuickYieldPayload } from '@/tolerancing/api';
import { withZemaxFetchSlot } from '@/lib/zemaxFetchLimiter';
import { normalizeAnalysisResponseBody } from '@/lib/analysisClient';
import { fetchParsed, parseContract, expectOk } from '@/lib/http';
import { formatFileSize, isStepImportFileName } from '@/lib/fileUtils';
import { CollabStateSchema, CompletedToursSchema, EditProposalListSchema, EditProposalMutationSchema, EXPORT_WARNING_COUNT_HEADER, type CompletedTours, type SystemExportFormat, type TourId, type WarningLevel } from '../../contracts/dist/index.js';
import type {
  AgentDefaultModelResponse,
  CollabState, Collaborator, EditProposal, PresenceMember, ProposalChange,
  ConversationRoleValue, CadFitRegion, CadImportReport, CadLightDirection, CadReferenceAxis,
  ChildRunState,
} from '../../contracts/dist/index.js';

/** The design-mode concept is retired and the current backend ignores this
 *  field entirely — but the frontend deploys (Vercel) ahead of the production
 *  backend (Railway), and the pre-retirement backend 400s a missing mode on
 *  conversation-creating routes. Keep sending it until the retired backend is
 *  gone from every environment — that is a deploy-ordering question, unrelated
 *  to migration 083 dropping the column. */
const AGENT_MODE = 'imaging';

/** Thrown when a save fails because the server's version has moved ahead of the client's. */
export class StaleVersionError extends Error {
  constructor() {
    super('Stale version');
    this.name = 'StaleVersionError';
  }
}

/** Why solve enforcement returned nothing about the design (`failure_class`
 *  on a failed `PATCH /systems/:id`, from `solveEnforcer.ts`). */
export type SaveFailureClass = 'service_timeout' | 'service_unreachable';

/** A save the engine never answered: nothing was written, and re-sending the
 *  same save can succeed. `message` keeps the backend's agent-facing text.
 *  Only Zemax and Optiland saves wait on solve enforcement: a CODE V save stores
 *  at once and re-resolves in the background (#7776), so it never fails this way. */
export class SaveNotPersistedError extends Error {
  constructor(message: string, readonly failureClass: SaveFailureClass) {
    super(message);
    this.name = 'SaveNotPersistedError';
  }
}

/** Thrown by submitAnswer/declineQuestion when the server no longer has the
 *  pending question (HTTP 404: timed out, already answered, or otherwise gone).
 *  Lets callers dismiss the stale popup without substring-matching error text. */
export class QuestionExpiredError extends Error {
  constructor() {
    super('Question not found or expired');
    this.name = 'QuestionExpiredError';
  }
}

/** Throw a descriptive Error from a non-ok response: prefer the body's `error`
 *  field, fall back to `fallback` (when the body isn't JSON), then the status.
 *  Callers handle any status-specific cases (404, 409, AGENT_BUSY) before
 *  delegating the generic failure here. Never returns. */
async function throwResponseError(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => ({ error: fallback }));
  // The server flattens the first schema issue into `error` on every route, so
  // the body's `error` is already the most specific message available.
  throw new Error(body.error ?? `HTTP ${response.status}`);
}

/** One unsupported Zemax-catalog glass surfaced by `/systems/import*` endpoints. */
export interface UnsupportedZemaxGlass {
  name: string;
  surface_index: number;
  /** Catalogs that DO contain this name — empty when the name is unknown
   * entirely, populated when the glass exists but isn't in the system's
   * declared glass_catalogs. */
  found_in_catalogs: string[];
  suggestions: string[];
}

/** One catalog entry for a glass, returned by GET /systems/glass-info.
 *  Backend ZemaxGlassEntry carries more fields (per-line indices, partial
 *  dispersions, chemical codes, etc.); the frontend only models what the
 *  popover renders. */
export interface GlassInfoEntry {
  name: string;
  catalog: string;
  status: string;
  is_procurable: boolean;
  Nd: number | null;
  Vd: number | null;
  min_wl_um: number | null;
  max_wl_um: number | null;
}

export interface GlassInfoResult {
  entries: GlassInfoEntry[];
  /** Set when the request itself failed (network, auth, 5xx). Empty `entries`
   * alone means the name genuinely isn't in any catalog — not an error. */
  error?: string;
}

/** One catalog glass on the nd–vd map (GET /systems/glass-catalog-map). */
export interface CatalogGlassPoint {
  name: string;
  catalog: string;
  nd: number;
  vd: number;
  dpgf: number | null;
  status: string;
}

/** Catalog-wide glass-map points for the glass-map overlay. Throws on failure —
 * the viewer treats that as "overlay unavailable" and reports it. */
export async function fetchCatalogGlassMap(): Promise<CatalogGlassPoint[]> {
  const response = await fetch(`${BACKEND_URL}/systems/glass-catalog-map`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Catalog map fetch failed (HTTP ${response.status})`);
  }
  const body = await response.json() as { glasses: CatalogGlassPoint[] };
  return body.glasses;
}

export async function fetchGlassInfo(name: string): Promise<GlassInfoResult> {
  try {
    const response = await fetch(`${BACKEND_URL}/systems/glass-info?name=${encodeURIComponent(name)}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      return { entries: [], error: `Lookup failed (HTTP ${response.status})` };
    }
    const body = await response.json() as { entries: GlassInfoEntry[] };
    return { entries: body.entries };
  } catch (e) {
    return { entries: [], error: e instanceof Error ? e.message : 'Network error' };
  }
}

let cachedToken: string | null = null;
let tokenFetchedAt = 0;
let pendingTokenRequest: Promise<string | null> | null = null;
const TOKEN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** Clear cached auth token (call on sign-out to prevent stale identity) */
export function clearAuthTokenCache() {
  cachedToken = null;
  tokenFetchedAt = 0;
  pendingTokenRequest = null;
}

export async function getAuthToken(): Promise<string | null> {
  const now = Date.now();
  if (cachedToken && now - tokenFetchedAt < TOKEN_CACHE_TTL) {
    return cachedToken;
  }

  if (pendingTokenRequest) return pendingTokenRequest;

  // Three invariants keep the shared slot safe, and all three are load-bearing:
  // the slot is assigned below before this function yields, so callers in the
  // same tick join this request instead of opening their own; it is cleared
  // once the request settles, so a FAILED request stays retryable rather than
  // handing its result to every later caller; and the fetch starts inside a
  // `then` so a `fetch` that throws outright still reaches the `catch` below,
  // because callers rely on this never throwing (see the 401 note in
  // lib/conversationMetaFailure.ts).
  const request: Promise<string | null> = Promise.resolve().then(() => fetch('/api/auth/token', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })).then(async (res) => {
    if (!res.ok) return null;
    const { token } = await res.json();
    // Signing out clears or replaces the slot; a response from before it must
    // not repopulate the cache for whoever is signed in now.
    if (pendingTokenRequest !== request) return null;
    cachedToken = token;
    tokenFetchedAt = Date.now();
    return token;
  }).catch(() => null).finally(() => {
    if (pendingTokenRequest === request) pendingTokenRequest = null;
  });
  pendingTokenRequest = request;
  return request;
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/** Auth headers without Content-Type. Needed for (a) multipart uploads, where the
 *  browser must set the boundary itself, and (b) body-less requests, which Fastify
 *  rejects with 400 (FST_ERR_CTP_EMPTY_JSON_BODY) if they claim application/json. */
export async function getAuthHeadersWithoutContentType(): Promise<Record<string, string>> {
  const headers = await getAuthHeaders();
  delete headers['Content-Type'];
  return headers;
}

function filesForJobRequest(files: FileAttachment[]): FileAttachment[] {
  return files.map((file) => ({
    id: file.id,
    name: file.name,
    type: file.type,
    size: file.size,
    data: file.data,
  }));
}

export async function startJob(
  conversationId: string,
  userMessage: string,
  files: FileAttachment[] = [],
  verbosity?: Verbosity,
  disabledTools: string[] = [],
  analysisEngine?: string,
  agentSource?: string,
  // Project a NEW conversation is created in (ignored once the conversation exists).
  projectId?: string,
  opts?: {
    /** Deploy-pause auto-resume: id of the deploy_paused run this send continues.
     *  The backend's atomic claim 409s (ALREADY_RESUMED) if another tab won. */
    resumeOfRunId?: string;
  }
): Promise<StartJobResponse> {
  const disabledCaps = getDisabledCapabilities();
  const mergedTools = [...new Set([...disabledCaps.tools, ...disabledTools])];
  const mergedCaps = { ...disabledCaps, tools: mergedTools };
  const hasDisabled = getDisabledCount(mergedCaps) > 0;
  const requestFiles = filesForJobRequest(files);
  const promptVersion = getPromptVersion();
  const engine = (analysisEngine as AnalysisEngine | undefined) ?? getSelectedService();
  // Same gate the capabilities-panel checkbox renders (see
  // meritSeparateBlockedReason): the merit path is Zemax-only, so the flag
  // rides only when the engine supports it. Inline is the server default;
  // merit_inline: false pins the separate merit side-agent.
  const meritSeparate = getMeritSeparate() && meritSeparateBlockedReason(engine) === null;
  const agentModel = getAgentModel();

  const headers = await getAuthHeaders();
  const body = JSON.stringify({
    conversation_id: conversationId,
    ...(projectId ? { project_id: projectId } : {}),
    ...(agentSource ? { agent_source: agentSource } : {}),
    intent: 'create',
    mode: AGENT_MODE,
    // Per-turn pin exists only for a draft's first send (hands-off before the
    // conversation row exists) and always-pin callers (zosapi/peek/eval
    // panels). Existing conversations omit it so the server resolves
    // verbosity from the durable conversations.verbosity column — a stale
    // tab or a send racing conversation-meta hydration must not silently
    // override a user's Minimal setting for that turn.
    ...(verbosity !== undefined ? { verbosity } : {}),
    analysis_engine: engine,
    ...(opts?.resumeOfRunId !== undefined ? { resume_of_run_id: opts.resumeOfRunId } : {}),
    params: {
      user_message: userMessage,
      files: requestFiles,
      ...(hasDisabled ? { disabled_capabilities: mergedCaps } : {}),
      ...(promptVersion ? { prompt_version: promptVersion } : {}),
      ...(meritSeparate ? { merit_inline: false } : {}),
      ...(agentModel ? { model: agentModel } : {})
    }
  });

  // The backend returns 503 while draining in-flight turns during a deploy. The
  // new deployment takes over within seconds, so retry briefly instead of
  // surfacing a transient failure — the retried send lands on the new container.
  let response = await fetch(`${BACKEND_URL}/jobs/start`, { method: 'POST', headers, body });
  for (let attempt = 0; response.status === 503 && attempt < 3; attempt++) {
    // Honor a valid Retry-After (including 0); fall back to 3s only when the
    // header is absent or unparseable — Number(null) is 0, so guard explicitly.
    const header = response.headers.get('Retry-After');
    const parsed = header === null ? NaN : Number(header);
    const retryAfterSec = Number.isFinite(parsed) && parsed >= 0 ? parsed : 3;
    await new Promise(resolve => setTimeout(resolve, retryAfterSec * 1000));
    response = await fetch(`${BACKEND_URL}/jobs/start`, { method: 'POST', headers, body });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start job' }));
    // The conversation already has a live turn (often one this client couldn't
    // see). Surface a typed error so the caller can handle it gracefully rather
    // than showing a generic failure.
    // Another tab already claimed this deploy-paused run — the resume happened,
    // just not here. Typed so the auto-resume effect can swallow it silently.
    if (response.status === 409 && error.code === 'ALREADY_RESUMED') {
      const resumed = new Error(error.error) as AlreadyResumedError;
      resumed.code = 'ALREADY_RESUMED';
      throw resumed;
    }
    if (response.status === 409 && error.code === 'AGENT_BUSY') {
      // Inside this guard the body is a real AGENT_BUSY 409, which always carries
      // `error` — no fallback (the JSON-parse fallback above has no `code`, so it
      // can't reach here). See the AGENT_BUSY 409 path in jobs.ts.
      const busy = new Error(error.error) as AgentBusyError;
      busy.code = 'AGENT_BUSY';
      busy.activeJobId = error.active_job_id;
      throw busy;
    }
    // The server flattens the first schema issue into `error`, so appending
    // `details` here would render the same message twice.
    throw new Error(error.error ?? `HTTP ${response.status}`);
  }

  return response.json();
}

/** Thrown by startJob when /jobs/start 409s an auto-resume: another tab won
 *  the atomic deploy_paused claim, so the resume already happened elsewhere. */
export interface AlreadyResumedError extends Error {
  code: 'ALREADY_RESUMED';
}

export function isAlreadyResumedError(err: unknown): err is AlreadyResumedError {
  return err instanceof Error && (err as AlreadyResumedError).code === 'ALREADY_RESUMED';
}

/** Newest run's terminal state — the deploy-pause auto-resume check. `run` is
 *  null for a conversation that has never run a turn. */
export async function getLastRun(
  conversationId: string,
): Promise<{ run: { id: string; status: string; error_code: string | null } | null }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/last-run`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/** Thrown by startJob when /jobs/start returns 409: a turn is already live. */
export interface AgentBusyError extends Error {
  code: 'AGENT_BUSY';
  activeJobId?: string;
}

export function isAgentBusyError(err: unknown): err is AgentBusyError {
  return err instanceof Error && (err as AgentBusyError).code === 'AGENT_BUSY';
}

export async function streamJob(jobId: string): Promise<EventSource> {
  const token = await getAuthToken();
  const url = token
    ? `${BACKEND_URL}/jobs/stream?job_id=${jobId}&token=${encodeURIComponent(token)}`
    : `${BACKEND_URL}/jobs/stream?job_id=${jobId}`;
  return new EventSource(url);
}

export async function submitAnswer(
  jobId: string,
  requestId: string,
  answers: Record<string, string>
): Promise<{ success: boolean }> {
  const response = await fetch(`${BACKEND_URL}/jobs/${jobId}/answer`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ requestId, answers })
  });

  if (response.status === 404) {
    throw new QuestionExpiredError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to submit answer');
  }

  return response.json();
}

export async function declineQuestion(
  jobId: string,
  requestId: string,
  reason?: string
): Promise<{ success: boolean }> {
  const response = await fetch(`${BACKEND_URL}/jobs/${jobId}/decline`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ requestId, reason })
  });

  if (response.status === 404) {
    throw new QuestionExpiredError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to decline question');
  }

  return response.json();
}

export interface ConversationRunSettings {
  engine: AnalysisEngine | null;
  verbosity: Verbosity;
  merit_inline: boolean | null;
  spec_judge_enabled: boolean;
}

/** Explicit per-conversation run-config edit. Requires an existing conversation
 *  row (drafts keep settings client-side until the first turn stamps them);
 *  engine is re-validated against hosted-Zemax access server-side and
 *  merit_inline is admin-only. */
export async function patchConversationSettings(
  conversationId: string,
  patch: Partial<ConversationRunSettings>
): Promise<ConversationRunSettings> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/settings`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.error ?? `Failed to update conversation settings (${response.status})`);
  }
  return response.json();
}

export async function updateJobVerbosity(jobId: string, verbosity: Verbosity): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/jobs/${jobId}/verbosity`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ verbosity })
  });
  if (!response.ok) throw new Error(`Failed to update verbosity: ${response.status}`);
}

export function parseSSEEvent(data: string): SSEEvent | null {
  try {
    return JSON.parse(data);
  } catch (e) {
    logger.error('Failed to parse SSE data:', data.slice(0, 200), e);
    return null;
  }
}

export async function getMessages(conversationId: string, agentSource?: string, tail?: number): Promise<ChatMessage[]> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (agentSource) params.set('agent_source', agentSource);
  if (tail !== undefined) params.set('tail', String(tail));
  const response = await fetch(`${BACKEND_URL}/messages?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    // 404 = conversation doesn't exist yet (new conversation before first message)
    if (response.status === 404) return [];
    await throwResponseError(response, 'Failed to get messages');
  }

  const data = await response.json();

  // Convert backend message format to UI format. The chat_messages schema
  // guarantees message_id, content (NOT NULL), and created_at — no fallbacks.
  return data.messages.map((msg: any) => ({
    id: msg.message_id,
    role: msg.role as 'user' | 'assistant' | 'system',
    content: msg.content,
    images: msg.images,
    timestamp: new Date(msg.created_at).getTime(),
    user_email: msg.user_email ?? undefined,
    authorLabel: msg.metadata?.author_source === 'main-agent' ? 'Main agent' : undefined,
    judge: msg.metadata?.goal_judge ? 'goal' : msg.metadata?.spec_judge ? 'spec' : undefined,
    streaming: msg.metadata?.streaming === true,
    interaction: msg.metadata?.interaction,
    mirrorBodyCheck: msg.metadata?.mirrorBodyCheck,
    sources: msg.metadata?.sources,
    codevAnalysisRuns: msg.metadata?.codev_analysis_runs,
    // Chat-attachment bytes are ephemeral (never persisted); metadata keeps
    // name/type/size so reloaded history still renders the chips. `data` is
    // empty by construction — nothing downstream reads it for history bubbles.
    attachments: msg.metadata?.attachments?.map(
      (a: { name: string; type: string; size: number }, i: number) => ({
        id: `${msg.message_id}-att-${i}`,
        name: a.name,
        type: a.type,
        size: a.size,
        data: '',
      }),
    ),
  }));
}

export async function getConversationImage(id: string, signal: AbortSignal): Promise<Blob> {
  const response = await fetch(`${BACKEND_URL}/conversation-images/${encodeURIComponent(id)}`, {
    headers: await getAuthHeaders(), signal,
  });
  if (!response.ok) await throwResponseError(response, 'Could not load image');
  return response.blob();
}

/** Admin-only: the conversation's tool/skill/subagent calls, rebuilt from the
 *  stored SDK transcript and keyed by the assistant bubble each fired under. */
export interface AgentTraceResponse {
  trace: Record<string, ToolCallRecord[]>;
}

export async function fetchAgentTrace(conversationId: string): Promise<AgentTraceResponse> {
  const response = await fetch(`${BACKEND_URL}/admin/conversations/${conversationId}/agent-trace`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to get agent trace');
  return response.json();
}

/**
 * Query-string fragment pinning a system read to a historical version.
 * Nullable so callers can forward `usePreviewVersion()` straight through — a
 * null preview must read the ACTIVE system, never version null.
 */
function versionQuery(version?: number | null): string {
  return version != null ? `&version=${version}` : '';
}

/**
 * Fetches a conversation's system. `version` selects a specific historical
 * version (read-only viewer preview); null/undefined means the live active
 * system.
 *
 * The version is deliberately a per-call argument rather than ambient state:
 * write paths (optimize, tolerancing, the merit editor) must keep reading the
 * active system, and an ambient version would silently bind them to a preview.
 */
export async function getCurrentSystem(conversationId: string, version?: number | null) {
  const response = await fetch(`${BACKEND_URL}/systems/current?conversation_id=${conversationId}${versionQuery(version)}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null; // No system yet
    }
    await throwResponseError(response, 'Failed to get system');
  }

  return response.json();
}

/** Export file formats the backend serves — single-sourced in contracts/src/vocabulary.ts. */
export type { SystemExportFormat } from '../../contracts/dist/index.js';

/** `version` mirrors {@link getCurrentSystem}: a read-only viewer previewing a
 *  historical version must download that version's file, not the live one. */
/**
 * Downloads the system in an engine's file format. `warningCount` is how many
 * conversion limitations the file itself lists (the server's count header);
 * 0 when the header is absent (a .zmx export lists none).
 */
export async function exportSystem(
  conversationId: string,
  version?: number | null,
  format: SystemExportFormat = 'zmx',
): Promise<{ blob: Blob; warningCount: number }> {
  const response = await fetch(`${BACKEND_URL}/systems/export?conversation_id=${conversationId}&format=${format}${versionQuery(version)}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to export system');
  }

  return {
    blob: await response.blob(),
    warningCount: Number(response.headers.get(EXPORT_WARNING_COUNT_HEADER) ?? 0),
  };
}

/**
 * Downloads a conversation's system as LLM JSON. `version` mirrors
 * {@link exportSystem}, so the two download buttons resolve the previewed
 * version identically.
 *
 * The server names the file (it knows the system title), so the parsed
 * `Content-Disposition` filename rides along with the blob; it is null when the
 * header is absent and the caller supplies its own name.
 */
export async function downloadSystemJson(
  conversationId: string,
  version?: number | null,
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await fetch(`${BACKEND_URL}/systems/download-json?conversation_id=${conversationId}${versionQuery(version)}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to download system JSON');
  }

  return {
    blob: await response.blob(),
    filename: response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] ?? null,
  };
}

/**
 * How long the server allows an upload's bytes to ARRIVE — a mirror of
 * REQUEST_TIMEOUT_MS in src/server/constants.ts (Node's `server.requestTimeout`,
 * enforced server-wide; the two are pinned equal by
 * tests/server/importLimits.test.ts). A file that cannot cross the wire inside
 * this window is cut off by Node with no reply, which the browser sees as a
 * network failure — `importTransportFailureMessage` names this allowance and
 * the file's size so the toast says what went wrong.
 */
export const CAD_IMPORT_UPLOAD_ALLOWANCE_MS = 900_000;

/**
 * The part of a STEP import's fetch budget that waits on the PARSE, once the
 * bytes are up. The top of the CAD timeout ladder (src/server/constants.ts,
 * the CAD_PARSE_TIMEOUT_MS block): the analysis service's child is killed at
 * 300 s and Node's fetch to it gives up at 330 s, so the browser must outlast
 * both to receive the `parse_timeout` refusal they produce rather than abort
 * first with nothing to show.
 */
export const CAD_IMPORT_PARSE_WAIT_MS = 360_000;

/**
 * How long the browser waits on an import request that carries a STEP file:
 * the upload allowance plus the parse wait, because a fetch's clock starts
 * before the first byte is sent, and budgeting the parse alone made the
 * browser abort a slow 100 MB upload the server would still have accepted.
 * Other import formats parse in-process in Node and keep the browser's default
 * (no timeout).
 */
export const CAD_IMPORT_FETCH_TIMEOUT_MS = CAD_IMPORT_UPLOAD_ALLOWANCE_MS + CAD_IMPORT_PARSE_WAIT_MS;

/** The fetch `signal` for an import upload: bounded only for a STEP file. */
function importSignal(file: File): AbortSignal | undefined {
  return isStepImportFileName(file.name) ? AbortSignal.timeout(CAD_IMPORT_FETCH_TIMEOUT_MS) : undefined;
}

/** Why an import request produced no readable answer, in the words the toast
 *  shows. A bare "Failed to fetch" told the user nothing about a 100 MB upload
 *  that Node cut off at the receive window, or a proxy that answered with an
 *  HTML error page instead of the route's JSON. */
export type ImportTransportFailure = 'network' | 'timeout' | { status: number };

export function importTransportFailureMessage(file: File, failure: ImportTransportFailure): string {
  const size = formatFileSize(file.size);
  if (failure === 'network') {
    return `Upload failed for ${file.name} (${size}). Check your connection or try a smaller file.`;
  }
  if (failure === 'timeout') {
    return `Import timed out after ${Math.round(CAD_IMPORT_FETCH_TIMEOUT_MS / 60_000)} minutes for ${file.name} (${size}). Retry on a faster connection or with a smaller file.`;
  }
  return `Import failed for ${file.name} (${size}) with HTTP ${failure.status}. Retry or use a smaller file.`;
}

/** Whether a rejected fetch was our own `AbortSignal.timeout` firing. */
function isAbortLike(err: unknown): boolean {
  const name = (err as { name?: unknown } | null)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/** POST an import's multipart body and read the route's JSON reply, turning
 *  the two ways that can fail without a reply — the fetch rejecting, or the
 *  body not being JSON — into a message that names the file, its size and the
 *  likely cause. The route's own `{ success:false, error }` replies are
 *  thrown unchanged. */
async function postImportForm<T extends { success: boolean; message?: string; detail?: string; error?: string }>(
  url: string,
  formData: FormData,
  file: File,
): Promise<T> {
  const headers = await getAuthHeadersWithoutContentType();
  let response: Response;
  try {
    response = await fetch(url, { method: 'POST', headers, body: formData, signal: importSignal(file) });
  } catch (err) {
    throw new Error(importTransportFailureMessage(file, isAbortLike(err) ? 'timeout' : 'network'));
  }
  let result: T;
  try {
    result = await response.json() as T;
  } catch {
    throw new Error(importTransportFailureMessage(file, { status: response.status }));
  }
  if (!response.ok || !result.success) {
    throw new Error(result.message ?? result.detail ?? result.error ?? `HTTP ${response.status}`);
  }
  return result;
}

export async function previewImport(
  conversationId: string,
  file: File,
  /** Project a NEW conversation is created in; ignored for existing conversations. */
  projectId?: string,
  /** CAD (STEP) uploads only: re-parse with this light direction instead of the
   *  heuristic's guess. Flipping it re-signs every radius, so the server
   *  re-parses rather than the modal flipping signs client-side. */
  lightDirection?: CadLightDirection,
  /** CAD (STEP) uploads only: the line the beam travels, in the STEP file's own
   *  world millimetres, plus what the window on it is for. Only a window
   *  (windshield, canopy, combiner) needs one — such a body has no axis of
   *  rotational symmetry to recover, so without a line the parse refuses the
   *  file. Supplying it turns axis fitting off, which is why it goes into the
   *  parse rather than being applied to its result. */
  referenceAxis?: CadReferenceAxis,
  /** CAD (STEP) uploads only, and only beside `referenceAxis`: the beam's
   *  footprint on the window — a disc of this radius about the strike point,
   *  inside which the face is fitted instead of over its whole trim. */
  fitRegion?: CadFitRegion,
): Promise<{
  success: boolean;
  preview?: {
    filename: string;
    num_surfaces: number;
    num_wavelengths: number;
    pupil: {
      mode: PupilMode;
      value?: number;
    };
    field_type: string;
    num_fields: number;
    wavelength_range?: string;
  };
  llm_json?: any;
  /** Lossy normalization heals (e.g. a dated catalog snapshot pin stripped to
   *  its base name) — shown in the preview modal before the user confirms. */
  warning?: string;
  /** The same parse warnings, structured. Confirm re-posts them: it receives
   *  the healed llm_json, so its own re-normalization no longer sees what the
   *  parse dropped, and this is how the list reaches the saved version's
   *  provenance.import_warnings. */
  normalization_warnings?: ImportNormalizationWarning[];
  unsupported_zemax_glasses?: UnsupportedZemaxGlass[];
  /** Present (possibly null) only for CAD uploads — what the STEP parse
   *  recovered and what it had to guess. Absent for .zmx/.json. */
  cad_import?: CadImportReport | null;
  error?: string;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('zmx_file', file);
  // Six floats and an enum, so it rides as a JSON part of the body rather than
  // as query params: the shape is `CadReferenceAxisSchema` in contracts/ and the
  // route validates it with exactly that schema.
  if (referenceAxis) formData.append('reference_axis', JSON.stringify(referenceAxis));
  if (fitRegion) formData.append('fit_region', JSON.stringify(fitRegion));

  // A CAD preview is parsed for the engine the import is headed for — a Zemax
  // conversation keeps a deliberately decentred element where the CAD put it,
  // optiland snaps it onto the axis — and a brand-new chat has no conversation
  // row for the server to read that from yet.
  const qs = new URLSearchParams({ conversation_id: conversationId, analysis_engine: getSelectedService() });
  if (projectId) qs.set('project_id', projectId);
  if (lightDirection) qs.set('light_direction', lightDirection);
  return postImportForm(`${BACKEND_URL}/systems/import/preview?${qs}`, formData, file);
}

export interface CustomCatalog {
  id: string;
  kind: 'glass' | 'coating';
  displayName: string;
  onDiskName: string;
  scope: 'org' | 'project' | 'conversation';
  glassNames: string[];
  status: 'pending' | 'ready' | 'invalid';
  validationError: string | null;
  byteSize: number;
  createdAt: string;
}

/** Backend catalog routes reply `{ error }` on failure — surface that message
 *  instead of a bare "HTTP <status>". */
async function customCatalogError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  return new Error(body?.error ?? `HTTP ${response.status}`);
}

/** Upload a custom catalog — glass (.AGF) or coating (.DAT); the backend derives
 *  the kind from the file extension. Scope selects who sees it: org-shared, the
 *  given project, or the given conversation. For conversation scope, `mode` +
 *  `projectId` let the backend create a draft chat's conversation row (the row
 *  doesn't exist until the first message) filed in the right project. */
export async function uploadCustomCatalog(
  file: File,
  scope: CustomCatalog['scope'],
  opts: { projectId?: string; conversationId?: string; displayName?: string } = {},
): Promise<CustomCatalog> {
  const formData = new FormData();
  formData.append('catalog', file);
  formData.append('scope', scope);
  if (opts.displayName) formData.append('display_name', opts.displayName);
  if (opts.projectId) formData.append('project_id', opts.projectId);
  if (opts.conversationId) formData.append('conversation_id', opts.conversationId);
  if (scope === 'conversation') formData.append('analysis_engine', getSelectedService());
  formData.append('mode', AGENT_MODE);

  const response = await fetch(`${BACKEND_URL}/custom-catalogs`, {
    method: 'POST',
    headers: await getAuthHeadersWithoutContentType(),
    body: formData,
  });
  if (!response.ok) throw await customCatalogError(response);
  return response.json();
}

/** The custom catalogs visible in a conversation (org + project + conversation).
 *  `projectId` is the page's project context so a draft chat (no conversation
 *  row yet) can still list its project's catalogs. */
export async function listCustomCatalogs(conversationId: string, projectId?: string): Promise<CustomCatalog[]> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (projectId) params.set('project_id', projectId);
  const response = await fetch(`${BACKEND_URL}/custom-catalogs?${params}`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) throw await customCatalogError(response);
  return response.json();
}

export async function deleteCustomCatalog(id: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/custom-catalogs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await getAuthHeadersWithoutContentType(),
  });
  if (!response.ok) throw await customCatalogError(response);
}

/** A user-uploaded ImageSimulation source image (conversation-scoped). The
 *  onDiskName token is what the analysis request's input_file carries. */
export interface SourceImage {
  id: string;
  conversationId: string;
  displayName: string;
  onDiskName: string;
  byteSize: number;
  createdAt: string;
}

/** Upload a source image (.bmp/.png/.jpg) for Image Simulation. The backend
 *  validates magic bytes and creates a draft chat's conversation row if needed.
 *  `projectId` is the page's project context so that auto-created row lands in
 *  the right project instead of the Scratch pad (same contract as
 *  uploadCustomCatalog); ignored for existing conversations. */
export async function uploadSourceImage(conversationId: string, file: File, projectId?: string): Promise<SourceImage> {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('conversation_id', conversationId);
  formData.append('analysis_engine', getSelectedService());
  if (projectId) formData.append('project_id', projectId);
  const response = await fetch(`${BACKEND_URL}/source-images`, {
    method: 'POST',
    headers: await getAuthHeadersWithoutContentType(),
    body: formData,
  });
  if (!response.ok) throw await customCatalogError(response);
  return response.json();
}

export async function listSourceImages(conversationId: string): Promise<SourceImage[]> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  const response = await fetch(`${BACKEND_URL}/source-images?${params}`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) throw await customCatalogError(response);
  return (await response.json()).source_images;
}

export async function deleteSourceImage(id: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/source-images/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await getAuthHeadersWithoutContentType(),
  });
  if (!response.ok) throw await customCatalogError(response);
}

/** Re-run worker validation for a catalog stuck at pending/invalid. */
export async function revalidateCustomCatalog(id: string): Promise<CustomCatalog> {
  const response = await fetch(`${BACKEND_URL}/custom-catalogs/${encodeURIComponent(id)}/revalidate`, {
    method: 'POST',
    headers: await getAuthHeadersWithoutContentType(),
  });
  if (!response.ok) throw await customCatalogError(response);
  return response.json();
}

/** One parse/normalization warning as the import preview reports it. */
export interface ImportNormalizationWarning {
  code: string;
  level: WarningLevel;
  message: string;
}

export async function importSystem(
  conversationId: string,
  file: File,
  preParsedJson?: any,
  /** Project a NEW conversation is created in; ignored for existing conversations. */
  projectId?: string,
  /** CAD (STEP) uploads only: the report the preview returned, persisted next to
   *  the system as `provenance.cad_import` so the agent can read what was guessed. */
  cadImport?: CadImportReport | null,
  /** The preview's parse warnings, persisted with the saved version. */
  normalizationWarnings?: ImportNormalizationWarning[],
): Promise<{
  success: boolean;
  system_id?: string;
  version?: number;
  structural_metadata?: {
    num_surfaces?: number;
    num_wavelengths?: number;
    pupil_mode?: string;
    pupil_value?: number;
    field_type?: string;
    num_fields?: number;
  };
  message?: string;
  warning?: string;
  error?: string;
  unsupported_zemax_glasses?: UnsupportedZemaxGlass[];
}> {
  const formData = new FormData();
  formData.append('zmx_file', file);

  if (preParsedJson) {
    formData.append('llm_json', JSON.stringify(preParsedJson));
    formData.append('filename', file.name);
  }

  if (cadImport) {
    formData.append('cad_import', JSON.stringify(cadImport));
  }

  if (normalizationWarnings?.length) {
    formData.append('normalization_warnings', JSON.stringify(normalizationWarnings));
  }

  const engine = getSelectedService();
  const qs = new URLSearchParams({ conversation_id: conversationId, mode: AGENT_MODE, analysis_engine: engine });
  if (projectId) qs.set('project_id', projectId);
  return postImportForm(`${BACKEND_URL}/systems/import?${qs}`, formData, file);
}

/**
 * The route merges `system` over the stored one, and JSON drops `undefined`
 * keys, so a field the editor cleared back to its default (ray aiming off,
 * global reference surface 1, …) would silently keep its old value. A null
 * top-level field tells the route to delete it. Surfaces are replaced whole,
 * not merged, so this applies to the system object only.
 */
export function systemResetsAsNull<T extends Record<string, unknown>>(system: T): T {
  return Object.fromEntries(Object.entries(system).map(([k, v]) => [k, v === undefined ? null : v])) as T;
}

export async function applySystemChanges(
  systemId: string,
  changes: {
    if_version: number;
    system?: any;
    surfaces?: any[];
    /** The beamsplitter arms; an empty array clears them. */
    beam_splits?: import('@/types/optical').BeamSplit[];
    title?: string;
    notes?: string;
    analysis_engine?: AnalysisEngine;
  }
) {
  const response = await fetch(`${BACKEND_URL}/systems/${systemId}`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      ...changes,
      ...(changes.system ? { system: systemResetsAsNull(changes.system) } : {}),
      analysis_engine: changes.analysis_engine ?? getSelectedService(),
    }),
  });

  // 404 means the system_id the client holds was soft-deleted (e.g. another
  // tab re-uploaded the file, which replaces the system). Semantically the
  // same as 409 from the user's perspective: their local state is based on a
  // system that no longer exists in its current form. Show the stale banner.
  if (response.status === 409 || response.status === 404) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to apply changes' }));
    // detail (the human-readable string from SolveEngineValidationError.message and
    // friends) reads better than the error-code slug; fall back to the slug only if
    // detail wasn't provided.
    const message = error.validation_errors?.length
      ? `Validation failed: ${error.validation_errors.join('; ')}`
      : error.message ?? error.detail ?? error.error ?? `HTTP ${response.status}`;
    if (error.failure_class) throw new SaveNotPersistedError(message, error.failure_class);
    throw new Error(message);
  }

  return response.json();
}

/**
 * LDE API: Create a new optical system for a conversation that doesn't have one yet.
 */
export async function createSystem(
  conversationId: string,
  params: {
    system: any;
    surfaces: any[];
    beam_splits?: import('@/types/optical').BeamSplit[];
    /** Project a NEW conversation is created in; ignored for existing conversations. */
    projectId?: string;
  }
) {
  const response = await fetch(`${BACKEND_URL}/systems`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      mode: AGENT_MODE,
      system: params.system,
      surfaces: params.surfaces,
      ...(params.beam_splits?.length ? { beam_splits: params.beam_splits } : {}),
      analysis_engine: getSelectedService(),
      ...(params.projectId ? { project_id: params.projectId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create system' }));
    const message = error.validation_errors?.length
      ? `Validation failed: ${error.validation_errors.join('; ')}`
      : error.message ?? error.detail ?? error.error ?? `HTTP ${response.status}`;
    if (error.failure_class) throw new SaveNotPersistedError(message, error.failure_class);
    throw new Error(message);
  }

  return response.json();
}

/**
 * Apply solver/optimization result with proper provenance tracking.
 * Uses the dedicated endpoint that records solver_name and source='solver'.
 */
export async function applySolverResult(
  systemId: string,
  params: {
    if_version: number;
    system?: any;
    surfaces: any[];
    solver_name: string;
    analysis_engine?: AnalysisEngine;
    optimization_diagnostics?: {
      method: 'local' | 'global' | 'hammer';
      algorithm: 'DLS' | 'OrthogonalDescent';
      merit_before: number | null;
      merit_after: number | null;
      stop_reason: 'cycles_complete' | 'timeout' | 'completed' | 'user_canceled' | 'solver_failed';
      solver_error: string | null;
      elapsed_seconds: number;
      // Mirrors OperandResultSchema (src/services/merit/types.ts), which the
      // route re-validates: `weight` is null on rows OpticStudio gives no
      // weight (BLNK/DMFS) or whose Weight cell read back non-finite.
      operand_results: Array<{
        type: string; target: number | null; value: number | null; delta?: number | null;
        weight: number | null; contribution_percent: number | null; readback_failed: boolean;
      }>;
      mfe_row_errors: Array<{ row_index: number; error: string }>;
      best_solutions: number[] | null;
      systems_evaluated: number | null;
    };
    // Top-N saved global design forms (geometry + merit each). Sent only on a
    // global optimize-complete so the backend persists the shelf (#2119/#2121);
    // omitted by single "Load this design" promotions, which must not overwrite it.
    solutions?: OptimizeSolution[];
  }
) {
  const response = await fetch(`${BACKEND_URL}/systems/${systemId}/apply-solver-result`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to apply solver result');
  }

  return response.json();
}

/** One surface's vertex in the drawn (global) frame — the drawing's x axis is
 *  global z. Keyed by LDE index (0 = object, only present for a finite object). */
export interface ZemaxSurfaceLayoutEntry {
  index: number;
  z: number;
  y: number;
  is_stop: boolean;
  is_object_plane: boolean;
  is_coordinate_break: boolean;
  semi_diameter: number;
  thickness: number | null;
  /** 3x3 local→global rotation (row-major); column 3 is the local +Z axis. */
  rotation: number[][];
}

export interface ZemaxCrossSectionResult {
  imageBase64: string;
  imageFormat: 'svg' | 'png';
  paraxial?: { efl?: number | null; fno?: number | null; lens_units?: string | null; [key: string]: unknown };
  raysThrough?: number | null;
  raysTotal?: number | null;
  /** Refractive index of the space following each surface at the primary
   *  wavelength, signed as OpticStudio reports it: negative in mirror space,
   *  after an odd mirror count. 1:1 with the request `surfaces` (object and
   *  image included); [] when the worker's index read failed.
   *  Feeds the element-slider focal-length control. */
  refractiveIndices: number[];
  /** The per-surface vertices the image was drawn from. Lets the element
   *  sliders hit-test against the drawn layout on folded (mirror) systems the
   *  client-side planner cannot place. */
  surfaceLayout: ZemaxSurfaceLayoutEntry[];
}

/** The Zemax `/cross-section-svg` success payload. */
interface ZemaxCrossSectionSvgPayload {
  image: string;
  image_format: 'svg' | 'png';
  paraxial?: ZemaxCrossSectionResult['paraxial'];
  rays_total?: number | null;
  rays_through?: number | null;
  refractive_indices: number[];
  surface_layout: ZemaxSurfaceLayoutEntry[];
}

type CrossSectionRoute = 'zemax' | 'codev';
const CROSS_SECTION_ENGINE_LABEL: Record<CrossSectionRoute, string> = { zemax: 'Zemax', codev: 'CODE V' };

/** The failure's message followed by every warning the service reported with
 *  it, in the service's order, under a "Warnings:" line so none reads as the reason. */
function withWarnings(message: string, warnings: { message: string }[]): string {
  return warnings.length === 0 ? message : `${message}\nWarnings:\n${warnings.map(warning => `- ${warning.message}`).join('\n')}`;
}

/** The message for a non-OK CODE V response: the proxy's or service's own
 *  `error` / `errors[].message` text (as `useZemaxAnalysis` reads it). A body
 *  with no readable error (an HTML error page, say) contributes nothing beyond
 *  the status. */
function codevFailureMessage(status: number, bodyText: string): string {
  const normalized = normalizeAnalysisResponseBody(bodyText);
  const messages = normalized?.success === false
    ? normalized.errors.map(error => error.message).filter(message => message.trim() !== '')
    : [];
  return messages.length === 0 ? `CODE V analysis service returned ${status}` : messages.join('; ');
}

/** POST a system to an engine's `/cross-section-svg` proxy route and return the
 *  success payload `P`. Throws on HTTP failure or a structured worker failure
 *  (no image); the latter carries the worker's error message. For CODE V a
 *  non-OK response carries the service's `error` / `errors[].message` text, and
 *  a 200 `{ success: false }` adds every warning the service reported. */
async function postCrossSectionSvg<P>(
  route: CrossSectionRoute,
  conversationId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<P> {
  const label = CROSS_SECTION_ENGINE_LABEL[route];
  // Same funnel as useZemaxAnalysis: the Zemax and CODE V workers are both
  // serial, so either engine's POST queues behind the shared limiter on
  // purpose. X-Session-Id lets the proxy resolve any custom-catalog (cust_*)
  // bytes the system references.
  const authHeaders = await getAuthHeaders();
  const response = await withZemaxFetchSlot(
    () =>
      fetch(`${BACKEND_URL}/api/${route}/cross-section-svg`, {
        method: 'POST',
        headers: { ...authHeaders, 'X-Analysis-Engine': getSelectedService(), 'X-Session-Id': conversationId },
        body: JSON.stringify(body),
        signal,
      }),
    signal,
  );
  if (!response.ok) {
    if (route === 'codev') throw new Error(codevFailureMessage(response.status, await response.text().catch(() => '')));
    throw new Error(`${label} analysis service returned ${response.status}`);
  }
  const data = await response.json();
  if (!(data.success && data.image)) {
    const message = data.errors?.[0]?.message ?? `${label} cross-section render failed`;
    throw new Error(route === 'codev' ? withWarnings(message, data.warnings) : message);
  }
  return data;
}

/**
 * POST a system to the body-driven Zemax `/cross-section-svg` endpoint and return
 * the layout image + paraxial metrics. Single source of the request contract
 * (URL, auth + engine headers, response field names) shared by the cross-section
 * viewer and the global-solutions gallery thumbnails (#2120).
 *
 * Throws on HTTP failure or a structured worker failure (no image) — the latter
 * carries the worker's error message so callers can surface it.
 */
export async function fetchZemaxCrossSection(
  conversationId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<ZemaxCrossSectionResult> {
  const data = await postCrossSectionSvg<ZemaxCrossSectionSvgPayload>('zemax', conversationId, body, signal);
  return {
    imageBase64: data.image,
    imageFormat: data.image_format,
    paraxial: data.paraxial,
    raysThrough: data.rays_through,
    raysTotal: data.rays_total,
    refractiveIndices: data.refractive_indices,
    surfaceLayout: data.surface_layout,
  };
}

/** A CODE V layout entry, keyed by CODE V surface number (`index`), plus the
 *  prescription row it was exported from: past a coordinate break the two differ. */
export type CodevSurfaceLayoutEntry = Omit<ZemaxSurfaceLayoutEntry, 'is_coordinate_break' | 'thickness'> & {
  authored_index: number;
};

/** Parsed RSI failure kind and native A/O/E flags, as `surface:flag`. */
export interface CodevRayFlags {
  field_index: number;
  wavelength_index: number;
  pupil_y: number;
  failure_mode: 'MISS' | 'TIR' | null;
  flags: string[];
}

/** The CODE V route's first-order properties: FIR's printed cell text per
 *  value, verbatim (e.g. "50.0000", "0.1000E+19", "Infinity"); `max_field` is
 *  the native field value text. Distances are in `lens_units`. */
export interface CodevCrossSectionParaxial {
  efl: string;
  bfl: string;
  fno: string;
  epd: string;
  exp: string;
  epl: string;
  exl: string;
  field_type: string;
  max_field: string;
  field_unit: string;
  lens_units: string;
}

/** The CODE V `/cross-section-svg` success payload. */
interface CodevCrossSectionSvgPayload {
  image: string;
  paraxial: CodevCrossSectionParaxial;
  rays_total: number;
  rays_through: number;
  surface_layout: CodevSurfaceLayoutEntry[];
  ray_flags: CodevRayFlags[];
  warnings: { field: string; message: string }[];
}

/** The CODE V route draws SVG only, so the result carries no image format. */
export interface CodevCrossSectionResult {
  imageBase64: string;
  paraxial: CodevCrossSectionParaxial;
  raysTotal: number;
  raysThrough: number;
  surfaceLayout: CodevSurfaceLayoutEntry[];
  rayFlags: CodevRayFlags[];
  /** The warnings the service reported with the render. */
  warnings: { field: string; message: string }[];
}

/** POST the current system to the CODE V `/cross-section-svg` route and return the drawing. */
export async function fetchCodevCrossSection(
  conversationId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<CodevCrossSectionResult> {
  const data = await postCrossSectionSvg<CodevCrossSectionSvgPayload>('codev', conversationId, body, signal);
  return {
    imageBase64: data.image,
    paraxial: data.paraxial,
    raysTotal: data.rays_total,
    raysThrough: data.rays_through,
    surfaceLayout: data.surface_layout,
    rayFlags: data.ray_flags,
    warnings: data.warnings,
  };
}

export interface SystemHistoryEntry {
  system_id: string;
  version: number;
  source: 'created' | 'retrieved' | 'cloned' | 'solver' | 'modified' | 'history_restore';
  title: string | null;
  created_at: string;
  is_active: boolean;
  version_name: string | null;
  surface_count: number;
  system_data?: import('@/types/optical').LLMOpticalSystem;
  provenance_summary: string;
  source_filename: string | null;
}

export interface SystemHistoryResult {
  conversation_id: string;
  total_versions: number;
  current_version: number | null;
  versions: SystemHistoryEntry[];
}

export async function getSystemHistory(
  conversationId: string,
  options?: { includeDeleted?: boolean; limit?: number }
): Promise<SystemHistoryResult> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (options?.includeDeleted !== undefined) {
    params.set('include_deleted', String(options.includeDeleted));
  }
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit));
  }

  const response = await fetch(`${BACKEND_URL}/systems/history?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to get history');
  }

  return response.json();
}

export async function restoreSystemVersion(
  conversationId: string,
  version: number,
  options?: { reason?: string; userEmail?: string }
): Promise<{
  success: boolean;
  system_id: string;
  version: number;
  restored_from: {
    system_id: string;
    version: number;
    created_at: string;
    source: string;
  };
}> {
  const response = await fetch(`${BACKEND_URL}/systems/restore`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      version,
      reason: options?.reason,
      user_email: options?.userEmail
    }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to restore version');
  }

  return response.json();
}

export async function updateVersionName(
  conversationId: string,
  version: number,
  name: string
): Promise<{ success: boolean; version: number; name: string | null }> {
  const response = await fetch(`${BACKEND_URL}/systems/version-name`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      version,
      name,
    }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to update version name');
  }

  return response.json();
}

/**
 * Quick Focus: Find and set the optimal image plane position via
 * POST /systems/quick-focus. The backend routes on `analysis_engine`
 * (read from the selected service), so the same wrapper serves both
 * engines — the response shape just differs:
 *   Optiland: thickness_before/after, delta, rms_before/after, …
 *   Zemax:    delta_thickness, original_thickness, new_thickness, lens_units
 * The Optiland-only tuning options are ignored by the Zemax path.
 */
export async function quickFocus(
  conversationId: string,
  options?: {
    focus_metric?: 'PARAXIAL' | 'BEST_RMS';
    bracket_mm?: number;
    tol_mm?: number;
    num_rays?: number;
  }
): Promise<{
  success: boolean;
  // Optiland engine fields
  thickness_before?: number;
  thickness_after?: number;
  delta?: number;
  rms_before?: number;
  rms_after?: number;
  num_fields?: number;
  num_wavelengths?: number;
  new_version?: number;
  // Zemax engine fields
  delta_thickness?: number;
  original_thickness?: number;
  new_thickness?: number;
  lens_units?: string;
  // Both engines: the solve lost to the refocus, plus the server-built
  // sentence explaining it — the same wording the agent is given.
  cleared_solve?: {
    surface_index: number;
    cell: 'radius' | 'thickness';
    from: string;
    message: string;
  };
  // Both engines: the solver's non-fatal warnings, flattened to their message
  // strings. Absent when the solve produced none.
  warnings?: string[];
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/systems/quick-focus`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        conversation_id: conversationId,
        analysis_engine: getSelectedService(),
        focus_metric: options?.focus_metric,
        bracket_mm: options?.bracket_mm,
        tol_mm: options?.tol_mm,
        num_rays: options?.num_rays,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      return { success: false, error: data.error ?? `Backend returned ${response.status}` };
    }

    return data;
  } catch (err: any) {
    return { success: false, error: `Quick Focus request failed: ${err.message}` };
  }
}

/**
 * Auto-calc vignetting: recompute per-field vignetting from the actual
 * apertures via POST /systems/recompute-vignetting. The backend routes on
 * `analysis_engine` (Optiland marginal-ray search, Zemax native SetVignetting,
 * both symmetric vx/vy; CODE V native SETVIG, which also sets vdx/vdy) and
 * persists the factors as a new system version.
 */
export async function recomputeVignetting(conversationId: string): Promise<{
  success: boolean;
  system_id?: string;
  new_version?: number;
  version_bumped?: boolean;
  fields?: Array<{ field_index: number; x: number; y: number; vx: number; vy: number; vdx?: number; vdy?: number }>;
  warnings?: string[];
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/systems/recompute-vignetting`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        conversation_id: conversationId,
        analysis_engine: getSelectedService(),
      }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      return { success: false, error: data.error ?? `Backend returned ${response.status}` };
    }
    return data;
  } catch (err: any) {
    return { success: false, error: `Auto-calc vignetting request failed: ${err.message}` };
  }
}

/**
 * Zemax Scale Lens — calls backend endpoint which handles the full flow:
 * getCurrentSystem → Zemax scale-lens → persistSolverResult
 */
export async function zemaxScaleLens(
  conversationId: string,
  scaleFactor: number,
): Promise<{
  success: boolean;
  scale_factor?: number;
  efl_before?: number;
  efl_after?: number;
  original_unit?: string;
  /** The solver's non-fatal warnings, flattened to their message strings.
   *  Absent when the solve produced none. */
  warnings?: string[];
  error?: string;
}> {
  let response: Response;
  try {
    response = await fetch(`${BACKEND_URL}/systems/scale-lens`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ conversation_id: conversationId, scale_factor: scaleFactor, analysis_engine: getSelectedService() }),
    });
  } catch (err: any) {
    return { success: false, error: `Network error: ${err.message}` };
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    return { success: false, error: body?.error ?? `HTTP ${response.status}` };
  }

  return response.json().catch(() => ({ success: false, error: 'Invalid response from server' }));
}

// ---------------------------------------------------------------------------
// Merit Function API
// ---------------------------------------------------------------------------

export interface MeritFunctionHistoryEntry {
  merit_function_id: string;
  version: number;
  source: 'optimize' | 'evaluate' | 'wizard' | 'agent' | 'ui' | 'imported';
  format: 'semantic' | 'raw' | 'wizard' | 'manual';
  created_at: string;
  is_active: boolean;
  num_rows: number | null;
  last_merit_value: number | null;
  system_version: number | null;
  notes: string | null;
}

export interface MeritFunctionHistoryResult {
  conversation_id: string;
  total_versions: number;
  current_version: number | null;
  versions: MeritFunctionHistoryEntry[];
}

/** Wire-format shape of a single merit function operand row (shared by
 * save/load APIs). A `type` (not `interface`) so it gets an implicit index
 * signature and assigns to `Record<string, unknown>` (TemplateConfig rows). */
export type OperandRowData = {
  operand_code: string;
  params: (number | null)[];
  // null preserves Zemax-emitted "no target/weight" rows (e.g. DMFS) across
  // the wire — matches the worker model (`zemax_worker/models/merit.py`:
  // EvaluatedOperandRow.target/weight defaults) and the agent-side
  // MeritOperandRow (`src/agent/tools/meritFunctionUtils.ts`).
  target: number | null;
  weight: number | null;
  comment?: string | null;
  // Identifier of the optimization-wizard run that produced this row;
  // null for manually-authored rows. Persists through JSONB as-is so the
  // frontend can tint wizard-generated blocks after reload.
  wizard_group_id?: string | null;
};

export interface MeritFunctionRecord {
  id: string;
  conversation_id: string;
  version: number;
  /** In-place write count of this version row; an in-place save sends it back as `ifRevision`. */
  revision: number;
  operand_rows: OperandRowData[] | null;
  format: 'semantic' | 'raw' | 'wizard' | 'manual';
  source: 'optimize' | 'evaluate' | 'wizard' | 'agent' | 'ui' | 'imported';
  last_merit_value: number | null;
  system_id: string | null;
  system_version: number | null;
  title: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Read the conversation's persisted top-N global-optimization design forms
 * (#2119/#2121). Returns [] when no global run has been saved — used by the
 * merit editor to repopulate the gallery on load/conversation-switch so the
 * forms survive page reloads.
 */
export async function getZemaxGlobalSolutions(conversationId: string): Promise<OptimizeSolution[]> {
  const response = await fetch(`${BACKEND_URL}/zemax/global-solutions?conversation_id=${conversationId}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 404) return [];
    await throwResponseError(response, 'Failed to get global solutions');
  }

  return response.json();
}

export async function getZemaxCurrentMeritFunction(conversationId: string): Promise<MeritFunctionRecord | null> {
  const response = await fetch(`${BACKEND_URL}/merit-functions/current?conversation_id=${conversationId}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    await throwResponseError(response, 'Failed to get merit function');
  }

  return response.json();
}

export async function getZemaxMeritFunctionHistory(
  conversationId: string,
  options?: { limit?: number }
): Promise<MeritFunctionHistoryResult> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit));
  }

  const response = await fetch(`${BACKEND_URL}/merit-functions/history?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to get merit function history');
  }

  return response.json();
}

export async function restoreZemaxMeritFunctionVersion(
  conversationId: string,
  version: number
): Promise<{
  success: boolean;
  merit_function_id: string;
  version: number;
  restored_from: {
    merit_function_id: string;
    version: number;
    created_at: string;
    source: 'optimize' | 'evaluate' | 'wizard' | 'agent' | 'ui' | 'imported';
  };
}> {
  const response = await fetch(`${BACKEND_URL}/merit-functions/restore`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, version }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to restore merit function');
  }

  return response.json();
}

export async function saveZemaxMeritFunctionRows(
  conversationId: string,
  operandRows: OperandRowData[],
  options: {
    lastMeritValue?: number | null;
    /** Update the active row without creating a new version (plain edits). */
    inPlace?: boolean;
    ifVersion?: number;
    /** In-place saves only: the revision the edited rows were loaded at, so a
     *  second tab's in-place save to the same version turns this one into a 409. */
    ifRevision?: number;
    /** (Re-)bind the saved function to this system id/version pair. Set by a
     * wizard rebuild (both values from the same getCurrentSystem fetch, so the
     * pair is consistent); omitted on plain edits so the in-place save
     * COALESCEs (preserves) the existing binding. */
    systemId?: string;
    systemVersion?: number;
    /** Label for version-bumping saves — a wizard rebuild passes 'wizard' so
     * history shows what produced the version. Server default is 'manual'. */
    format?: 'wizard';
    signal?: AbortSignal;
  } = {},
): Promise<{ id: string; version: number; revision: number }> {
  const response = await fetch(`${BACKEND_URL}/merit-functions/save`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    signal: options.signal,
    body: JSON.stringify({
      conversation_id: conversationId,
      analysis_engine: getSelectedService(),
      mode: AGENT_MODE,
      operand_rows: operandRows,
      last_merit_value: options.lastMeritValue,
      ...(options.inPlace ? { in_place: true } : {}),
      ...(options.ifVersion !== undefined ? { if_version: options.ifVersion } : {}),
      ...(options.ifRevision !== undefined ? { if_revision: options.ifRevision } : {}),
      ...(options.systemId !== undefined ? { system_id: options.systemId } : {}),
      ...(options.systemVersion !== undefined ? { system_version: options.systemVersion } : {}),
      ...(options.format !== undefined ? { format: options.format } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to save merit function');
  }

  return response.json();
}

export async function clearZemaxMeritFunction(
  conversationId: string,
  ifVersion?: number,
  signal?: AbortSignal,
): Promise<{ success: boolean; id?: string; version?: number; already_empty?: boolean }> {
  const response = await fetch(`${BACKEND_URL}/merit-functions/clear`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    signal,
    body: JSON.stringify({
      conversation_id: conversationId,
      ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to clear merit function');
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Tolerance Function API
// ---------------------------------------------------------------------------

export type ToleranceFunctionSource = 'manual' | 'agent' | 'ui' | 'imported';

export interface ToleranceFunctionHistoryEntry {
  tolerance_function_id: string;
  version: number;
  source: ToleranceFunctionSource;
  created_at: string;
  is_active: boolean;
  num_rows: number | null;
  system_version: number | null;
}

export interface ToleranceFunctionHistoryResult {
  conversation_id: string;
  total_versions: number;
  current_version: number | null;
  versions: ToleranceFunctionHistoryEntry[];
  truncated?: boolean;
}

/** Wire-format shape of a single tolerance row (snake_case for the API).
 * A `type` (not `interface`) so it gets an implicit index signature and
 * assigns to `Record<string, unknown>` (TemplateConfig rows). */
export type ToleranceRowApiData = {
  operand_code: string;
  params: (number | null)[];
  min_value: number | null;
  max_value: number | null;
  ignore_during_tolerancing: boolean;
  do_not_adjust_during_inverse: boolean;
  comment?: string | null;
};

export interface ToleranceFunctionRecord {
  id: string;
  conversation_id: string;
  version: number;
  operand_rows: ToleranceRowApiData[] | null;
  source: ToleranceFunctionSource;
  system_id: string | null;
  system_version: number | null;
  title: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function getZemaxCurrentToleranceFunction(conversationId: string): Promise<ToleranceFunctionRecord | null> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/current?conversation_id=${conversationId}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    await throwResponseError(response, 'Failed to get tolerance function');
  }

  return response.json();
}

export async function getZemaxToleranceFunctionHistory(
  conversationId: string,
  options?: { limit?: number },
): Promise<ToleranceFunctionHistoryResult> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (options?.limit !== undefined) params.set('limit', String(options.limit));

  const response = await fetch(`${BACKEND_URL}/tolerance-functions/history?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to get tolerance function history');
  }

  return response.json();
}

export async function restoreZemaxToleranceFunctionVersion(
  conversationId: string,
  version: number,
): Promise<{
  success: boolean;
  tolerance_function_id: string;
  version: number;
  restored_from: {
    tolerance_function_id: string;
    version: number;
    created_at: string;
    source: ToleranceFunctionSource;
  };
}> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/restore`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, version }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to restore tolerance function');
  }

  return response.json();
}

export async function saveZemaxToleranceFunctionRows(
  conversationId: string,
  operandRows: ToleranceRowApiData[],
  inPlace?: boolean,
  ifVersion?: number,
  /** When set, (re-)bind the saved function to this system id/version pair.
   * Used by a wizard rebuild to re-pin to the current system (both values from
   * the same getCurrentSystem fetch, so the pair is consistent — staleness keys
   * on system_id); omitted on plain edits so the in-place save COALESCEs
   * (preserves) the existing binding. */
  repin?: { systemId: string; systemVersion: number },
  signal?: AbortSignal,
): Promise<{ id: string; version: number }> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/save`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    signal,
    body: JSON.stringify({
      conversation_id: conversationId,
      analysis_engine: getSelectedService(),
      mode: AGENT_MODE,
      operand_rows: operandRows,
      ...(inPlace ? { in_place: true } : {}),
      ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
      ...(repin !== undefined ? { system_id: repin.systemId, system_version: repin.systemVersion } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to save tolerance function');
  }

  return response.json();
}

/** One row a `.TOL` import could not persist, with the reason the service gives. */
export interface DroppedToleranceRow {
  row: number;
  operand_code: string;
  reason: string;
}

/**
 * Persist the rows a `.TOL` import produced, as a NEW version — the tolerance
 * function it replaces stays restorable from history. Rows whose operand this
 * service refuses are dropped and returned in `dropped` rather than failing the
 * whole file; the refused set lives in src/services/tolerance/saver.ts.
 */
export async function importToleranceFunction(
  conversationId: string,
  operandRows: ToleranceRowApiData[],
  ifVersion?: number,
  repin?: { systemId: string; systemVersion: number },
): Promise<{ id: string; version: number; dropped: DroppedToleranceRow[] }> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/import`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      analysis_engine: getSelectedService(),
      operand_rows: operandRows,
      ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
      ...(repin !== undefined ? { system_id: repin.systemId, system_version: repin.systemVersion } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to import tolerance file');
  }

  return response.json();
}

/**
 * Persist a UI-initiated tolerance run (#2457) so `generateToleranceBrief` reuses
 * it instead of re-running. Best-effort — the backend stores it only when these
 * rows still match the persisted tolerance function (returns `persisted: false`
 * with a reason otherwise), and callers fire-and-forget. `operandRows` are the
 * exact rows the run executed; `result` is the compute payload, stored opaquely.
 * `systemId`/`systemVersion` are the system the run EXECUTED against, captured
 * before the run — the backend keys the stored run to these rather than re-reading
 * the current system at POST time, so a version bump in the window between the run
 * resolving and this POST landing can't mislabel a stale result as current (#2494).
 */
export async function persistToleranceRunResult(
  conversationId: string,
  params: ToleranceComputeRunParams,
  operandRows: ToleranceApiRow[],
  result: ComputeToleranceAnalysisPayload,
  systemId: string,
  systemVersion: number,
): Promise<{ persisted: boolean; reason?: string }> {
  // The brief reuses stats/yield_curve/sensitivity only; drop the bulky fields it
  // never reads (saved_systems holds base64 .zmx files) so the stored JSONB stays small.
  const storedResult: Record<string, unknown> = { ...result };
  delete storedResult.saved_systems;
  delete storedResult.text_output;
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/run-result`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, params, operand_rows: operandRows, result: storedResult, system_id: systemId, system_version: systemVersion }),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to persist tolerance run result');
  }
  return response.json();
}

/** Persist a UI-initiated Quick Yield run into its own 'quick' slot (#2881) so
 * the panel restores it on revisit. Same rows-match/versions contract as
 * {@link persistToleranceRunResult}; quick's params carry only the MC knobs
 * (the estimator's criterion is baked server-side). */
export async function persistQuickYieldRunResult(
  conversationId: string,
  params: { num_runs: number; mc_statistic: string },
  operandRows: ToleranceApiRow[],
  result: QuickYieldPayload,
  systemId: string,
  systemVersion: number,
): Promise<{ persisted: boolean; reason?: string }> {
  const storedResult: Record<string, unknown> = { ...result };
  delete storedResult.text_output;
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/run-result`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, run_kind: 'quick', params, operand_rows: operandRows, result: storedResult, system_id: systemId, system_version: systemVersion }),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to persist quick-yield run result');
  }
  return response.json();
}

/** A stored tolerance run as returned by GET /tolerance-functions/run-result.
 * `result` is the opaque analysis payload the run produced (compute or quick
 * shape per `run_kind`); `is_current` is the server's verdict — exact
 * tolerance-function + system version match and not invalidated by an
 * in-place row edit. */
/** A stored run WITHOUT its payload — identity, provenance, and the server's
 * currency verdict. Enough to re-badge staleness and label the kind chips. */
export interface StoredToleranceRunMeta {
  run_kind: 'compute' | 'inverse' | 'quick';
  params: Record<string, unknown>;
  source: 'agent' | 'ui';
  system_version: number | null;
  tolerance_function_version: number;
  invalidated: boolean;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

/** A stored run with the analysis payload the views render. */
export interface StoredToleranceRunDto extends StoredToleranceRunMeta {
  result: Record<string, unknown>;
}

export interface ToleranceRunResultsResponse<R extends StoredToleranceRunMeta = StoredToleranceRunDto> {
  runs: R[];
  current: { system_version: number | null; tolerance_function_version: number | null };
}

/** Fetch the conversation's stored tolerance runs (one per kind, newest first)
 * with per-run currency verdicts. The tolerance panel hydrates from this on
 * mount and on tolerance_run_update SSE events (#2881).
 *
 * `metaOnly` omits the result payloads: a stored run's blob is ~20 KB and up,
 * and the panel re-checks currency on every system change, so the staleness
 * path must not drag the payloads across the wire to recompute two booleans. */
export async function getToleranceRunResults(conversationId: string): Promise<ToleranceRunResultsResponse>;
export async function getToleranceRunResults(conversationId: string, metaOnly: true): Promise<ToleranceRunResultsResponse<StoredToleranceRunMeta>>;
export async function getToleranceRunResults(
  conversationId: string,
  metaOnly?: true,
): Promise<ToleranceRunResultsResponse<StoredToleranceRunMeta>> {
  const response = await fetch(
    `${BACKEND_URL}/tolerance-functions/run-result?conversation_id=${encodeURIComponent(conversationId)}${metaOnly ? '&meta=1' : ''}`,
    { headers: await getAuthHeaders() },
  );
  if (!response.ok) {
    await throwResponseError(response, 'Failed to fetch tolerance run results');
  }
  return response.json();
}

export async function clearZemaxToleranceFunction(
  conversationId: string,
  ifVersion?: number,
): Promise<{ success: boolean; id?: string; version?: number; already_empty?: boolean }> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/clear`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to clear tolerance function');
  }

  return response.json();
}

/** Result of a UI-triggered tolerance brief. Mirrors the `runToleranceBrief`
 *  summary the generateToleranceBrief agent tool returns. */
export interface ZemaxToleranceBriefResult {
  success: boolean;
  file_id: string;
  file_name: string;
  criterion: string;
  as_built_mean: number | null;
  as_built_std: number | null;
  /** The orchestrator's warnings about the run the brief packages — the same
   *  `ToleranceApiWarning` entries `warningsBanner` renders for every other
   *  tolerancing call; absent when the run reported none. */
  warnings?: ToleranceApiWarning[];
  note: string;
}

/** Generate the ME-readable tolerance brief for the conversation's current
 *  tolerance function (fresh sensitivity + Monte Carlo, saved to the project
 *  file library). Runs an expensive worker analysis, so the caller shows a
 *  loading state. The engine follows the active Zemax service selection. */
export async function generateZemaxToleranceBrief(
  conversationId: string,
): Promise<ZemaxToleranceBriefResult> {
  const response = await fetch(`${BACKEND_URL}/tolerance-functions/brief`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, engine: getSelectedService() }),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to generate tolerance brief');
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// ZOS-API Script API
// ---------------------------------------------------------------------------

export interface ZosapiScriptRecord {
  id: string;
  version: number;
  script_text: string;
  source: 'agent' | 'ui';
  title: string | null;
  notes: string | null;
  zmx_files: Array<{ name: string; content: string }> | null;
  created_at: string;
  updated_at: string;
}

export async function getZosapiScript(conversationId: string): Promise<ZosapiScriptRecord | null> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  const response = await fetch(`${BACKEND_URL}/zosapi-scripts/current?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    await throwResponseError(response, 'Failed to get ZOS-API script');
  }

  return response.json();
}

export async function saveZosapiScript(
  conversationId: string,
  scriptText: string,
  inPlace?: boolean,
  zmxFiles?: Array<{ name: string; content: string }> | null,
  ifVersion?: number,
  ifUpdatedAt?: string,
  signal?: AbortSignal,
): Promise<{ id: string; version: number; updated_at: string }> {
  const response = await fetch(`${BACKEND_URL}/zosapi-scripts/save`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    signal,
    body: JSON.stringify({
      conversation_id: conversationId,
      analysis_engine: getSelectedService(),
      mode: AGENT_MODE,
      script_text: scriptText,
      ...(inPlace ? { in_place: true } : {}),
      // Omit-to-preserve: the server clears files on explicit null but
      // carries them forward when the key is absent.
      ...(zmxFiles !== undefined ? { zmx_files: zmxFiles } : {}),
      ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
      ...(ifUpdatedAt !== undefined ? { if_updated_at: ifUpdatedAt } : {}),
    }),
  });

  if (response.status === 409) {
    throw new StaleVersionError();
  }
  if (!response.ok) {
    await throwResponseError(response, 'Failed to save ZOS-API script');
  }

  return response.json();
}

export interface ZosapiScriptHistoryEntry {
  script_id: string;
  version: number;
  source: 'agent' | 'ui';
  title: string | null;
  created_at: string;
  is_active: boolean;
}

export interface ZosapiScriptHistoryResult {
  total_versions: number;
  current_version: number | null;
  versions: ZosapiScriptHistoryEntry[];
}

export async function getZosapiScriptHistory(
  conversationId: string,
  options?: { limit?: number }
): Promise<ZosapiScriptHistoryResult> {
  const params = new URLSearchParams({ conversation_id: conversationId });
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit));
  }

  const response = await fetch(`${BACKEND_URL}/zosapi-scripts/history?${params}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to get script history');
  }

  return response.json();
}

export async function restoreZosapiScriptVersion(
  conversationId: string,
  scriptId: string
): Promise<{
  script_id: string;
  version: number;
  restored_from: {
    script_id: string;
    version: number;
    created_at: string;
    source: 'agent' | 'ui';
  };
}> {
  const response = await fetch(`${BACKEND_URL}/zosapi-scripts/restore`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ conversation_id: conversationId, id: scriptId }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to restore script version');
  }

  return response.json();
}

export async function updateZosapiScriptTitle(
  conversationId: string,
  scriptId: string,
  title: string
): Promise<{ success: boolean; version: number; title: string | null }> {
  const response = await fetch(`${BACKEND_URL}/zosapi-scripts/title`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      id: scriptId,
      title,
    }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to update script title');
  }

  return response.json();
}

// =============================================================================
// Optimization
// =============================================================================

/**
 * One saved global-optimization solution returned on the optimize `complete`
 * event when the run opted in via `num_solutions` (#2119). rank 1 is the single
 * best — its `system`/`surfaces` equal `modified_system`/`modified_surfaces`.
 * `system`/`surfaces` are a ready-to-use pair: post them to the body-driven
 * Zemax analysis endpoints, or pass to `applySolverResult` to promote.
 */
export interface OptimizeSolution {
  rank: number;
  merit: number;
  system: import('@/types/optical').LLMOpticalSystem['system'];
  surfaces: import('@/types/optical').LLMOpticalSystem['surfaces'];
}

export interface RunZemaxOptimizationParams {
  conversationId: string;
  // Caller-generated (crypto.randomUUID()) so the caller can target the run
  // with stopZemaxOptimization while it streams. Also keys the worker's in-run
  // merit recorder — without it the convergence curve never draws.
  optimizationId: string;
  operandRows: Array<{ operand_code: string; params: (number | null)[]; target: number | null; weight: number | null }>;
  method: 'local' | 'global' | 'hammer';
  algorithm: 'DLS' | 'OrthogonalDescent';
  cycles?: number;
  timeoutSeconds?: number;
  numToSave?: number;
  // Global only: ask the orchestrator to return the top-N saved solutions
  // (geometry + merit each) on the complete event so the UI can show the
  // distinct design forms. Omitted = single best only (today's behavior).
  numSolutions?: number;
}

/**
 * Run optimization via the analysis service SSE endpoint.
 *
 * Returns the raw Response so the caller can read the SSE stream.
 * The response streams `data: {...}` events of types: init, complete, error.
 *
 * System persistence is handled by the backend (`persistSolverResult`) —
 * but since the frontend calls the analysis service directly (not the agent),
 * we need to persist via `applySystemChanges()` after receiving `modified_system`
 * and `modified_surfaces` from the complete event.
 */
export async function runZemaxOptimization(
  params: RunZemaxOptimizationParams,
): Promise<Response> {
  const systemData = await getCurrentSystem(params.conversationId);
  if (!systemData || !systemData.system_data) {
    throw new Error('No optical system found. Design a system first.');
  }

  const requestBody: Record<string, unknown> = {
    ...systemData.system_data,
    method: params.method,
    algorithm: params.algorithm,
    operand_rows: params.operandRows,
    // The merit editor persists/edits Op# row references 1-based (#1555), so the
    // operand_rows we forward carry 1-based Op# values. Declare the convention to
    // the worker — matching the agent's index_base=1 — or it would read them as
    // 0-based and constrain every Op#-referencing operand one row too low.
    index_base: 1,
    // Keys the worker's in-run merit recorder AND the cancel-watcher flag
    // (see stopZemaxOptimization). Caller-generated so the caller holds the
    // id; the UUID hyphens are within the worker's ^[a-zA-Z0-9_-]+$ pattern.
    optimization_id: params.optimizationId,
  };

  // Method-specific params
  if (params.method === 'local' && params.cycles !== undefined) {
    requestBody.cycles_per_batch = params.cycles;
  }
  if (params.timeoutSeconds !== undefined) {
    requestBody.timeout_seconds = params.timeoutSeconds;
  }
  if (params.method === 'global' && params.numToSave !== undefined) {
    requestBody.num_to_save = params.numToSave;
  }
  if (params.method === 'global' && params.numSolutions !== undefined) {
    requestBody.num_solutions = params.numSolutions;
  }

  const response = await fetch(`${BACKEND_URL}/api/zemax/optimize-step/stream`, {
    method: 'POST',
    // X-Session-Id lets the proxy resolve custom-catalog (cust_*) bytes the
    // system references before forwarding to the worker.
    headers: { ...await getAuthHeaders(), 'X-Analysis-Engine': getSelectedService(), 'X-Session-Id': params.conversationId },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(error.errors?.[0]?.message ?? error.error ?? `Analysis service returned ${response.status}`);
  }

  return response;
}

/** Signal the in-flight UI optimization to cancel. Success means the worker
 * ACCEPTED the signal — its cancel watcher polls at ~0.5s, after which the run
 * ends normally through the stream with stop_reason 'user_canceled' (partial
 * progress is kept and persisted). Same request shape as the agent path
 * (src/agent/tools/optimizationLock.ts). */
export async function stopZemaxOptimization(
  conversationId: string,
  optimizationId: string,
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${BACKEND_URL}/api/zemax/optimize-step/stop`, {
    method: 'POST',
    headers: { ...await getAuthHeaders(), 'X-Analysis-Engine': getSelectedService(), 'X-Session-Id': conversationId },
    body: JSON.stringify({ optimization_id: optimizationId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    return { success: false, error: error.errors?.[0]?.message ?? error.error ?? `HTTP ${response.status}` };
  }
  const data = await response.json() as { success: boolean; error?: string | null };
  return { success: data.success, error: data.error ?? undefined };
}

export interface TraceConversationItem {
  conversation_id: string;
  first_message: string | null;
  created_at: string;
  updated_at: string;
}

export async function getTraceConversations(): Promise<TraceConversationItem[]> {
  const response = await fetch(`${BACKEND_URL}/evals/trace-conversations`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to get trace conversations');
  }
  const data = await response.json();
  return data.conversations;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: await getAuthHeadersWithoutContentType(),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to delete conversation');
  }
}

export async function generateConversationTitle(
  conversationId: string,
  userMessage: string
): Promise<{ title: string | null }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/title`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ user_message: userMessage }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to generate title');
  }

  return response.json();
}

export async function renameConversation(
  conversationId: string,
  title: string
): Promise<{ title: string }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/title`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ title }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to rename conversation');
  }

  return response.json();
}

/** Re-file a chat — and its whole design tree — into another project the
 *  caller owns. Pairs with PATCH /conversations/:id/project. */
export async function moveConversation(
  conversationId: string,
  projectId: string
): Promise<{ project_id: string; moved_conversation_ids: string[] }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/project`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ project_id: projectId }),
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to move conversation');
  }

  return response.json();
}

/** The guided tours this account has already started. */
export async function getCompletedTours(): Promise<CompletedTours> {
  return fetchParsed(
    CompletedToursSchema,
    `${BACKEND_URL}/users/tours`,
    { headers: await getAuthHeaders() },
    'completed tours',
  );
}

export async function markTourCompleted(tour: TourId): Promise<CompletedTours> {
  // No body: a JSON content-type on an empty POST is a 400 from Fastify.
  return fetchParsed(
    CompletedToursSchema,
    `${BACKEND_URL}/users/tours/${tour}`,
    { method: 'POST', headers: await getAuthHeadersWithoutContentType() },
    'record tour',
  );
}

/** The caller's organization, or null when they belong to none. */
export async function getUserOrganization(): Promise<{ id: string; name: string } | null> {
  const response = await fetch(`${BACKEND_URL}/users/organization`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user organization: ${response.status}`);
  }

  return response.json();
}

export async function updateUserLanguage(
  language: Locale
): Promise<{ success: boolean; language: string }> {
  const response = await fetch(`${BACKEND_URL}/users/language`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ language })
  });

  if (!response.ok) {
    throw new Error(`Failed to update language: ${response.statusText}`);
  }

  return response.json();
}

export async function stopOptimization(
  conversationId: string
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/stop-optimization`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: '{}'
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to stop optimization');
  }

  return response.json();
}

/**
 * Interrupt the running Claude SDK query for a conversation.
 * Stops the active optimization and aborts the SDK query so the agent
 * doesn't continue processing. Best-effort: failures are logged but not
 * thrown — the user pressed ESC and shouldn't be blocked on a network
 * round-trip. Returns whether the server acknowledged the interrupt
 * (404 counts: no active run means there is nothing left to stop), so
 * callers that need to show "stop failed" can distinguish it from
 * "stop is taking a moment to unwind".
 */
export async function interruptAgent(conversationId: string): Promise<boolean> {
  try {
    const response = await fetch(`${BACKEND_URL}/jobs/interrupt`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ conversation_id: conversationId }),
    });
    if (response.ok || response.status === 404) return true;
    logger.warn('[agentApi] interruptAgent: server returned', response.status);
    return false;
  } catch (err) {
    logger.warn('[agentApi] interruptAgent failed:', err);
    return false;
  }
}

export async function copyConversation(
  conversationId: string
): Promise<{ new_conversation_id: string; warning?: string }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/copy`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: '{}',
  });

  if (!response.ok) {
    await throwResponseError(response, 'Failed to copy conversation');
  }

  return response.json();
}

/** Collaboration fork: the caller's own branch of a shared conversation.
 *  `system` is the fork's live optical system (the clone of the parent's), so
 *  the edit that triggered the fork can be applied to it straight away. */
export async function forkConversation(
  conversationId: string
): Promise<{ new_conversation_id: string; system: { id: string; version: number } | null }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/fork`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: '{}',
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to create branch');
  }
  return response.json();
}

export interface ForkSummary {
  id: string;
  title: string | null;
  user_email: string;
  forked_from_version: number | null;
  created_at: string;
  updated_at: string;
  is_mine: boolean;
}

/** Mirrors GET /conversations/:id/forks. */
export interface ForkFamily {
  /** The shared conversation the forks hang off: the parent when `conversationId`
   *  is itself a fork, otherwise the conversation itself. */
  root_id: string;
  /** Set only when the conversation is a fork. `readable` is false once the
   *  parent was deleted or un-shared — the switcher renders it inert. */
  parent: { id: string; title: string | null; user_email: string | null; readable: boolean } | null;
  /** Forks of the root the caller may see: their own, plus everyone's when
   *  they own the root. */
  forks: ForkSummary[];
}

// --- Collaboration: invites, presence, proposals -----------------------------

/**
 * Shapes and their runtime schemas come from contracts/, not from a hand copy:
 * these responses are contract-covered, so a field that drifts is caught by the
 * contract gate rather than by a crash in the review dialog.
 *
 * Every response below parses. The frontend deploys ahead of the backend, so a
 * newly-shipped client can meet a server that has not learned a field yet —
 * parsing turns that into a load error the dialog reports, instead of a
 * TypeError that takes the whole page down with it.
 *
 * The two proposal mutations keep their own non-ok branch rather than going
 * through `fetchParsed`: the server's refusal text ("you hold the pen", "someone
 * decided first", the versions in a stale refusal) is shown to the user
 * verbatim, and `expectOk` would bury it under a "<what> failed (409)" prefix.
 * They parse the success body with `parseContract`, which is the half of
 * `fetchParsed` that enforces the contract.
 */
export type { CollabState, Collaborator, EditProposal, PresenceMember, ProposalChange };
export type ConversationRole = ConversationRoleValue;

/** Report where this person is and get the room back in the same round trip. */
export async function sendPresence(
  conversationId: string,
  where: { active_tab: string | null; focus_key: string | null },
): Promise<CollabState> {
  return fetchParsed(
    CollabStateSchema,
    `${BACKEND_URL}/conversations/${conversationId}/presence`,
    { method: 'POST', headers: await getAuthHeaders(), body: JSON.stringify(where) },
    'presence',
  );
}

export async function leavePresence(conversationId: string): Promise<void> {
  // keepalive: sent from pagehide, so without it the browser may cancel the
  // request with the page and the pen waits out the TTL instead of moving on.
  await fetch(`${BACKEND_URL}/conversations/${conversationId}/presence`, {
    method: 'DELETE',
    headers: await getAuthHeadersWithoutContentType(),
    keepalive: true,
  });
}

export async function inviteCollaborator(
  conversationId: string,
  email: string,
  role: 'viewer' | 'editor',
): Promise<{ collaborator: Collaborator; email_sent: boolean }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/collaborators`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to invite');
  return response.json();
}

export async function removeCollaborator(conversationId: string, email: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/collaborators`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ email }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to remove collaborator');
}

export async function listProposals(
  conversationId: string,
  status?: 'pending' | 'approved' | 'rejected',
): Promise<EditProposal[]> {
  const qs = status ? `?status=${status}` : '';
  return (await fetchParsed(
    EditProposalListSchema,
    `${BACKEND_URL}/conversations/${conversationId}/proposals${qs}`,
    { headers: await getAuthHeaders() },
    'proposals',
  )).proposals;
}

export async function submitProposal(
  conversationId: string,
  body: { kind: EditProposal['kind']; base_version: number | null; payload: unknown; note?: string },
): Promise<EditProposal> {
  // Proposed system edits use the same explicit reset representation as PATCH.
  const payload = body.payload;
  if (body.kind === 'system' && payload !== null && typeof payload === 'object' && 'system' in payload
    && payload.system !== null && typeof payload.system === 'object') {
    body = { ...body, payload: { ...payload, system: systemResetsAsNull(payload.system as Record<string, unknown>) } };
  }
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/proposals`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to submit the change');
  return parseContract(EditProposalMutationSchema, await response.json(), 'submit proposal').proposal;
}

export async function decideProposal(
  proposalId: string,
  decision: 'approve' | 'reject',
): Promise<EditProposal> {
  const response = await fetch(`${BACKEND_URL}/proposals/${proposalId}/${decision}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: '{}',
  });
  if (!response.ok) await throwResponseError(response, `Failed to ${decision} the change`);
  return parseContract(EditProposalMutationSchema, await response.json(), `${decision} proposal`).proposal;
}

/** One node of the collaboration-branch tree. Distinct from `TreeNode`, which
 *  models same-owner design-tree lineage — the two relationships are rendered
 *  in separate views on purpose. */
export interface BranchNode {
  id: string;
  title: string | null;
  user_email: string;
  /** Version of the parent this branch was cut from. */
  forked_from_version: number | null;
  /** This branch's own live design version, so the tree can show how far it has
   *  moved since it was cut. */
  current_version: number | null;
  created_at: string;
  updated_at: string;
  children: BranchNode[];
}

export async function getBranchTree(
  conversationId: string,
): Promise<{ root_id: string; tree: BranchNode | null }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/branch-tree`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to load the branch tree');
  }
  return response.json();
}

export async function getConversationForks(conversationId: string): Promise<ForkFamily> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/forks`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to load branches');
  }
  return response.json();
}

export interface TreeNode {
  id: string;
  title: string | null;
  parent_conversation_id: string | null;
  project_id: string;
  status: 'running' | 'idle' | 'error';
  updated_at: string;
  decision_id: string | null;
  decision_name: string | null;
  variant_name: string | null;
  branch_origin: 'user' | 'agent' | null;
  children: TreeNode[];
  engine: AnalysisEngine;
}

export async function getConversationTree(rootId?: string): Promise<TreeNode[]> {
  const params = new URLSearchParams();
  if (rootId) params.set('root_id', rootId);
  const url = `${BACKEND_URL}/conversations/tree${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { headers: await getAuthHeaders() });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load tree' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  const json = await response.json();
  return json.tree;
}

// --- Child agents (spawned branches) ---------------------------------------

export interface ChildAgent {
  label: string;
  conversation_id: string;
  purpose?: string;
  state: ChildRunState;
  elapsed_seconds?: number;
  error?: string;
  active_system?: { version: number; title: string | null; moved_past_spawn_state: boolean };
  recent_messages: { role: string; content: string }[];
}

/** Per direct child of this conversation: run state, elapsed time, purpose —
 *  the same snapshot the agent's readAgents tool sees. */
export async function getChildAgents(conversationId: string): Promise<{ agents: ChildAgent[] }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/agents`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load child agents' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  return response.json();
}

/** Re-run a failed/stopped child agent's kickoff on the same child conversation. */
export async function respawnChildAgent(conversationId: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/respawn-agent`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    // getAuthHeaders sets Content-Type: application/json — Fastify rejects a
    // bodyless POST under that content type.
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to re-spawn child agent' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
}

/** One lineage node's live-agent snapshot — mirrors backend LineageAgentSnapshot. */
export interface LineageAgent {
  conversation_id: string;
  parent_conversation_id: string | null;
  decision_id: string | null;
  label: string;
  state: ChildRunState;
  /** Latest run is a fan-out kickoff — the node is acting as an agent. */
  kickoff: boolean;
  elapsed_seconds?: number;
  error?: string;
  activity?: { role: string; text: string; streaming: boolean };
}

/** Live run state + one-line activity for every node in this conversation's
 *  lineage (root included) — the design tree's living layer. */
export async function getLineageAgents(conversationId: string): Promise<{ agents: LineageAgent[] }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/lineage-agents`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load lineage agents' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  return response.json();
}

/** Count of the user's child agents currently queued/running, across all
 *  conversations — backs the rail badge. */
/** The deployment's default agent model — what a turn runs on with no pin. */
export async function getDefaultAgentModel(): Promise<string> {
  const response = await fetch(`${BACKEND_URL}/agent/default-model`, {
    headers: await getAuthHeaders(),
  });
  await expectOk(response, 'Load default agent model');
  return ((await response.json()) as AgentDefaultModelResponse).model;
}

export async function getActiveAgentCount(): Promise<number> {
  const response = await fetch(`${BACKEND_URL}/agents/active-count`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load active agent count' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.count;
}

/**
 * The whole design tree this conversation belongs to, rooted at the top of its
 * lineage (so every explored path is visible, not just descendants of `id`).
 */
export async function getDesignTree(conversationId: string): Promise<{ root_id: string; tree: TreeNode[] }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/design-tree`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load design tree' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  return response.json();
}

// --- Cross-section metrics ------------------------------------------------

/** The metric's RMS across the system's DEFINED fields × DEFINED wavelengths —
 *  the same field/wavelength set the cross-section Spot and WFE matrix cards
 *  show (mirrors the backend RmsByField). */
export interface RmsByField {
  /** Defined-field coordinates (signed radial position) in the same unit as
   *  paraxial.field.unit, in field-definition order. */
  field_coordinates: number[];
  /** Per-wavelength series keyed by wavelength (µm string), index-aligned with
   *  field_coordinates. A null entry = the metric couldn't be computed at that
   *  field/wavelength (e.g. every spot ray failed there). */
  series: Record<string, (number | null)[]>;
  /** Key into `series` for the PRIMARY wavelength (chart emphasis); null when
   *  unresolved. */
  primary: string | null;
}

/**
 * Engine-agnostic cross-section metrics cached per system version.
 * Mirrors the backend blob `CrossSectionMetrics`
 * (`src/server/services/crossSectionMetrics/types.ts`) field-for-field so no
 * mapping layer is needed.
 */
export interface CrossSectionMetrics {
  engine: AnalysisEngine;
  /** Blob schema version. Required: the backend serves a blob only on an exact
   *  version match, so anything older reads as absent and rebuilds. */
  schema_version: number;
  /** Canonical prescription hash. */
  source_hash: string;
  computed_at: string; // ISO 8601
  element_count: number | null;
  paraxial: {
    efl: number | null;
    bfl: number | null;
    fno: number | null;
    na: number | null;
    total_track: number | null;
    /** CODE V's FIR cells and native lens unit, without numeric reformatting. */
    printed?: { efl: string; bfl: string; fno: string; lens_units: string };
    field: { type: 'fov' | 'image_height' | 'object_height'; value: number | null; unit: string } | null;
  };
  throughput: { total_rays: number; rays_passed: number; rays_clipped: number; rays_obscuration: number } | null;
  rms: {
    /** RMS spot matrix, centroid-referenced on both engines — matching the
     *  in-node Spot card. Only one reference is stored, so spot never follows
     *  the WFE reference toggle. */
    spot: RmsByField | null;
    /** RMS WFE matrices per reference-sphere centering. `chief` is null when the
     *  engine reports no chief value (Optiland is centroid-convention only).
     *  For a centered system the two coincide on-axis; off-axis chief ≥ centroid
     *  (both remove piston; only centroid also removes tilt). A consumer must
     *  never substitute one for the other — chief-referenced numbers appearing
     *  under a centroid label is the exact defect this split exists to fix. */
    wfe: { centroid: RmsByField | null; chief: RmsByField | null } | null;
  };
  /** Base64-encoded layout SVG, cached with the metrics so the design tree can
   *  draw the real layout. Absent when the render failed or on blobs cached
   *  before SVG capture — the design tree shows a "No cross-section" placeholder
   *  in that case. */
  layout_svg?: string;
}

type CrossSectionMetricsResult =
  | { status: 'fresh'; metrics: CrossSectionMetrics }
  | { status: 'stale' | 'missing' | 'no_system'; metrics: null };

/** Fetch (building and caching on miss) the cross-section metrics for a conversation. */
export async function getCrossSectionMetrics(conversationId: string, engine: AnalysisEngine): Promise<CrossSectionMetricsResult> {
  const params = new URLSearchParams({ engine });
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/cross-section-metrics?${params}`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load cross-section metrics' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  return response.json();
}

// --- Decision branching ---------------------------------------------------

export interface VariantInput {
  name: string;
  instruction?: string;
}

export interface VariantResult {
  name: string;
  conversation_id: string | null;
  status: 'created' | 'error';
  error?: string;
}

/**
 * Create a decision at a node's tail with its initial variants.
 * `run=false` → lazy variants the user drives; `run=true` → agent explores each now.
 */
export async function createDecision(
  conversationId: string,
  params: { name: string; rationale?: string; variants: VariantInput[]; run: boolean; analysis_engine?: string },
): Promise<{ decision_id: string; variants: VariantResult[] }> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/decisions`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to create decision' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
  return response.json();
}

/** Rename a variant node (updates variant_name, the branch's label in the tree). */
export async function renameVariant(conversationId: string, variantName: string): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/variant`, {
    method: 'PATCH',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ variant_name: variantName }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to rename branch' }));
    throw new Error(err.error ?? `HTTP ${response.status}`);
  }
}

// ── Conversation cost (admin-only chat-header readout) ──────────────────────

export interface ConversationModelCost {
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
}

export interface ConversationCost {
  cost_usd: number;
  /** Recorded queries (main turns + side-agent runs); 0 before the first
   *  completed turn or for conversations that predate cost tracking. */
  turns: number;
  by_model: Record<string, ConversationModelCost>;
}

/** Admin-only (the backend 403s everyone else). */
export async function getConversationCost(conversationId: string): Promise<ConversationCost> {
  const response = await fetch(`${BACKEND_URL}/conversations/${conversationId}/cost`, {
    headers: await getAuthHeadersWithoutContentType(),
  });
  if (!response.ok) {
    await throwResponseError(response, 'Failed to get conversation cost');
  }
  return response.json();
}
